import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { fmtCurrency } from "@/lib/constants";
import { HardHat, ChevronDown, ChevronRight } from "lucide-react";

export default function CrewSpendCard() {
  const [open, setOpen] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const { data } = useQuery({
    queryKey: ["crew-costs"],
    queryFn: async () => (await api.get("/project/crew-costs")).data,
    refetchInterval: 60000,
  });
  const months = data?.months || [];
  if (!months.length) return null;
  const max = Math.max(...months.map((m) => m.total), 1);
  return (
    <div className="border border-slate-200 bg-white industrial-shadow rounded-sm" data-testid="crew-spend-card">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3" data-testid="crew-spend-toggle">
        <span className="stamp-label text-slate-600 flex items-center gap-1.5"><HardHat size={13} className="text-cyan-600" /> Scheduled crew spend — cash flow</span>
        <span className="flex items-center gap-3">
          <span className="font-mono font-bold text-sm text-slate-900" data-testid="crew-spend-total">{fmtCurrency(data.total)}</span>
          {open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 space-y-1.5">
          {months.map((m) => (
            <div key={m.month}>
              <button type="button" onClick={() => setExpanded(expanded === m.month ? null : m.month)}
                className="w-full grid grid-cols-[90px_1fr_110px] items-center gap-3 text-left group" data-testid={`crew-spend-month-${m.month}`}>
                <span className="text-xs font-semibold text-slate-700">{m.label}</span>
                <span className="h-4 bg-slate-100 rounded-sm overflow-hidden">
                  <span className="block h-full bg-cyan-600 group-hover:bg-cyan-700 transition-colors" style={{ width: `${Math.max(2, (m.total / max) * 100)}%` }} />
                </span>
                <span className="font-mono text-xs font-semibold text-slate-900 text-right tabular-nums">{fmtCurrency(m.total)}</span>
              </button>
              {expanded === m.month && (
                <div className="ml-[102px] mt-1 mb-2 border border-slate-200 rounded-sm divide-y divide-slate-100" data-testid={`crew-spend-detail-${m.month}`}>
                  {m.jobs.map((j) => (
                    <div key={j.name} className="flex items-center justify-between px-2.5 py-1.5 text-[11px]">
                      <span className="text-slate-700 font-semibold">{j.name}</span>
                      <span className="font-mono text-slate-600">{fmtCurrency(j.cost)}</span>
                    </div>
                  ))}
                  {m.crews.length > 0 && (
                    <div className="px-2.5 py-1.5 text-[10px] text-slate-500">
                      {m.crews.map((c) => `${c.name} ${fmtCurrency(c.cost)}`).join(" · ")}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          <p className="text-[10px] text-slate-400 pt-1">
            Man-days crew billing only, spread across each task's scheduled working days — moves automatically when the schedule changes. PO-billed crews are tracked through purchase orders.
          </p>
        </div>
      )}
    </div>
  );
}
